import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableFunctionsService } from '@services/table/table-functions.service';
import '@angular/localize/init';
import { ComponentBase } from '@abstracts/component-base';
import { Injector } from '@angular/core';
import { StripeInfosService } from '@services/entity/stripe-infos.service';
import { PricingsService } from '@services/entity/pricings.service';
import { PricingDto } from '@dto/pricingDto';
import { plainToClass } from 'class-transformer';
import { UsersService } from '@services/entity/users.service';
import { StripeInfoDto } from '@dto/stripeInfoDto';
import { PaymentsService } from '@services/payments/payments.service';
import { UserDto } from '@dto/userDto';
import { CustomersService } from '@services/entity/customers.service';
import { ToastService } from '@services/toast/toast.service';
import { BillingMethodEnum } from '@enums/billingMethodEnum.enum';
import { NgxSpinnerService } from 'ngx-spinner';
import { InvoicesService } from '@services/entity/invoices.service';
import { InvoiceSettingsDto } from '@dto/invoiceSettingsDto';

@Component({
  selector: 'app-pricings',
  templateUrl: './pricings.component.html',
  styleUrls: ['./pricings.component.scss']
})
export class PricingsComponent extends ComponentBase implements OnInit {

  public activeComponentClassName: string = 'PricingsComponent'

  public className = 'PricingsComponent';
  public activeTab = 'pricings';
  public user: UserDto;
  public users: UserDto[];
  public isUser: boolean = false;
  public isCustomer: boolean = false;
  public isOperator: boolean = false;
  public isSupporter: boolean = false;
  public stripeInfo: StripeInfoDto;

  public pricings: PricingDto[];
  public activeInvoicePricings: PricingDto[] = [];
  public activeAbonnementPricings: PricingDto[] = [];
  public activeQRCodePricings: PricingDto[] = [];
  public invoiceSettings: InvoiceSettingsDto = null;
  public isInvoiceSettingsValid: boolean = false;
  public isOnboardingCompleted: boolean = false;
  public isOnboardingStarted: boolean = false;
  public isLoaded: boolean = false;
  private loadCounter: number = 0;
  spinner1 = 'pricing';
  spinner2 = 'automatic';
  spinner3 = 'qrcode';

  public isTestingOrDevStage: boolean = false;

  constructor(
    public tableFunctions: TableFunctionsService,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public usersService: UsersService,
    public customersService: CustomersService,
    public pricingsService: PricingsService,
    public stripeInfosService: StripeInfosService,
    private paymentsService: PaymentsService,
    private invoicesService: InvoicesService,
    private toastService: ToastService,
    private spinner: NgxSpinnerService,

    injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.getRoles();
    this.isTestingOrDevStage = (window.location.host == 'cubos-connect-testing.azurewebsites.net') || (window.location.host == 'cubos-connect-dev.azurewebsites.net') || (window.location.host == 'localhost:44304')

    if (this.isOperator) {
      this.customersService.getMasterUserOfCustomer(this.activatedRoute.parent.snapshot.params.key)
        .subscribe(masterUser => {
          localStorage.setItem('masterUserId', masterUser.id);
          this.usersService.getUserDtoById(masterUser.id).subscribe(this.handleUser.bind(this));
        });
    } else {
      this.usersService.getLoggedInUser().subscribe(this.handleUser.bind(this));
    }

    this.pricingsService.pricingCreatedEvent.subscribe(pricings => {
      if (pricings[1] != null) {
        this.pricings = this.pricings.filter(p => p.id != pricings[1].id)
      }
      this.pricings.push(pricings[0])
      this.updateActivePricings()
    });

    this.pricingsService.pricingUpdatedEvent.subscribe(pricing => {
      const index = this.pricings.map(p => p.id).indexOf(pricing.id)
      this.pricings[index] = pricing
      this.updateActivePricings()
    });
  }

  handleUser(user) {
    this.user = user;
    this.getPricings();
    this.getStripeInfo();
    this.getInvoiceSettings();
  }

  private incrementAndCheckLoadCounter() {
    this.loadCounter += 1; // Increment the counter each time a call completes
    if (this.loadCounter >= 3) { // Check if all calls have completed
      this.isLoaded = true; // All HTTP requests have completed
    }
  }

  private updateActivePricings() {
    this.activeInvoicePricings = this.getInvoicePricings().filter(p => p.selectedUsers != null && p.selectedUsers.length > 0);
    this.activeAbonnementPricings = this.getAbonnementPricings().filter(p => p.selectedUsers != null && p.selectedUsers.length > 0);

    const qrcp = this.getQRCodePricings()
    if (qrcp.length > 0) this.activeQRCodePricings = [qrcp[qrcp.length - 1]]

  }

  private getPricings() {
    if (!this.user) return false
    this.pricingsService.getPricingsOfUser(this.user.id).subscribe(result => {
      this.pricings = plainToClass(PricingDto, result)

      if (this.pricings) {
        for (const p of this.pricings) {
          if (this.isInvoicePricing(p)) p.type = 'Rechnung'
          if (this.isAbonnementPricing(p)) p.type = 'Abonnement'
          if (this.isQRCodePricing(p)) p.type = 'QR-Code'
        }
      }
      this.updateActivePricings()
      this.incrementAndCheckLoadCounter()
    }, error => {
      console.error("Could not get Pricings.")
      this.incrementAndCheckLoadCounter()
    })
  }

  public getStripeInfo() {
    this.stripeInfosService.getStripeInfoOfUser(this.user.id).subscribe(stripeInfo => {
      this.stripeInfo = stripeInfo
      this.updateOnboardingState()
      if (this.isOnboardingStarted && !this.isOnboardingCompleted) {
        this.stripeInfosService.createStripeOnboardingUrl(this.user.id, this.stripeInfo.stripeAccountId).subscribe(result => {
          if (result.stripeOnboardingUrl) {
            this.stripeInfo.stripeOnboardingUrl = result.stripeOnboardingUrl;
          }
        }, error => {
          console.error("Could not create Stripe onboarding URL.");
        });
      } else if (this.isOnboardingCompleted) {
        this.stripeInfosService.createStripeDashboardUrl(this.user.id, this.stripeInfo.stripeAccountId).subscribe(result => {
          if (result.stripeDashboardUrl) {
            this.stripeInfo.stripeDashboardUrl = result.stripeDashboardUrl;
          }
        }, error => {
          console.error("Could not create Stripe dashboard URL.");
        });
      }
      this.incrementAndCheckLoadCounter()
    }, error => {
      console.error("Could not get StripeInfo.");
      this.incrementAndCheckLoadCounter()
    })
  }

  updateOnboardingState() {
    const hasAccountId = this.stripeInfo && this.stripeInfo.stripeAccountId && this.stripeInfo.stripeAccountId.trim() !== '';

    this.isOnboardingCompleted = hasAccountId && (this.stripeInfo.stripeOnboardingUrl === '');
    this.isOnboardingStarted = hasAccountId;
  }

  getInvoiceSettings() {
    this.invoicesService.getInvoiceSettingsOfUser(this.user.id).subscribe({
      next: (invoiceSettings) => {
        this.invoiceSettings = plainToClass(InvoiceSettingsDto, invoiceSettings);
        this.isInvoiceSettingsValid = !!this.invoiceSettings?.vatId; // Simplified truthy check
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.incrementAndCheckLoadCounter()
      }
    });
  }

  public getInvoicePricings() { return this.pricings.filter(p => this.isInvoicePricing(p)) }
  public getAbonnementPricings() { return this.pricings.filter(p => this.isAbonnementPricing(p)) }
  public getQRCodePricings() { return this.pricings.filter(p => this.isQRCodePricing(p)) }

  public isInvoicePricing(p: PricingDto) { return p.billingMethod == BillingMethodEnum.Manual }
  public isAbonnementPricing(p: PricingDto) { return p.billingMethod == BillingMethodEnum.Automatic }
  public isQRCodePricing(p: PricingDto) { return p.billingMethod == BillingMethodEnum.QRCode }

  private getRoles() {
    if (this.authenticationService.userHasRole('User')) {
      this.isUser = true;
    }

    if (this.authenticationService.userHasRole('Customer')) {
      this.isCustomer = true;
    }

    if (this.authenticationService.userHasRole('Operator')) {
      this.isOperator = true;
    }
    if (this.authenticationService.userHasRole('Supporter')) {
      this.isSupporter = true;
    }
  }

  setPricingType(type) {
    localStorage.setItem('pricingType', type)
    this.router.navigate(['create'], { relativeTo: this.activatedRoute });
  }

  public createStripeAccount() {
    this.paymentsService.createStripeAccount(this.user).subscribe(result => {
      window.open(result.url, '_self')
    }, error => { console.error(error) });
  }

  public onRouterOutletActivate(event: any) {
    this.activeComponentClassName = event.className;
  }

  navigateTo(url: string) {
    window.open(url, '_self');
  }

  public testMonthEnd() // for test monthly auto billing
  {
    this.toastService.loading('Abrechnung gestartet')
    this.pricingsService.testMonthEndForCustomer(this.user.id).subscribe(() => {
      this.toastService.success('Abrechnung erfolgreich', 0, true)
    }, error => {
      this.toastService.error('Abrechnung fehlgeschlagen', 0, true)
      console.error(error)
    });
  }
}
